import axiosInstance from 'axiosInstance';

const PUSH_PERMISSION_ASKED_PREFIX = 'gestodoPushPermissionAsked';
const PUSH_CONFIG_CACHE_MS = 5 * 60 * 1000;
const PUSH_ATTACH_CACHE_MS = 10 * 60 * 1000;

let pushConfigCache = null;
let pushConfigFetchedAt = 0;
let pushConfigPromise = null;
const pushAttachInFlightByUser = new Map();
const pushAttachLastRunAtByUser = new Map();

const getPermissionAskedKey = userId => `${PUSH_PERMISSION_ASKED_PREFIX}:${userId || 'anonymous'}`;

const hasAskedPermission = userId => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem(getPermissionAskedKey(userId)) === 'true';
  } catch (error) {
    return false;
  }
};

const markPermissionAsked = userId => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(getPermissionAskedKey(userId), 'true');
  } catch (error) {
    return;
  }
};

const urlBase64ToUint8Array = base64String => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

const isPushSupported = () => (
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window
);

const getPushConfig = async () => {
  const now = Date.now();
  if (pushConfigCache && now - pushConfigFetchedAt < PUSH_CONFIG_CACHE_MS) {
    return pushConfigCache;
  }
  if (pushConfigPromise) {
    return pushConfigPromise;
  }

  pushConfigPromise = axiosInstance.get('notifications/push/config')
    .then(response => {
      pushConfigCache = response.data || {};
      pushConfigFetchedAt = Date.now();
      return pushConfigCache;
    })
    .finally(() => {
      pushConfigPromise = null;
    });

  return pushConfigPromise;
};

export const attachPushSubscription = async userId => {
  const attachKey = String(userId || 'anonymous');
  const now = Date.now();
  const lastRunAt = pushAttachLastRunAtByUser.get(attachKey) || 0;
  if (now - lastRunAt < PUSH_ATTACH_CACHE_MS) {
    return { status: 'recently_checked' };
  }
  if (pushAttachInFlightByUser.has(attachKey)) {
    return pushAttachInFlightByUser.get(attachKey);
  }

  const attachPromise = attachPushSubscriptionNow(userId)
    .finally(() => {
      pushAttachInFlightByUser.delete(attachKey);
      pushAttachLastRunAtByUser.set(attachKey, Date.now());
    });

  pushAttachInFlightByUser.set(attachKey, attachPromise);
  return attachPromise;
};

const attachPushSubscriptionNow = async userId => {
  if (!isPushSupported()) {
    return { status: 'unsupported' };
  }

  const config = await getPushConfig();
  if (!config.enabled) {
    return { status: 'disabled' };
  }

  const vapidPublicKey = String(config.vapid_public_key || '').trim();
  if (!vapidPublicKey) {
    return { status: 'missing_vapid_public_key' };
  }

  if (Notification.permission === 'default' && !hasAskedPermission(userId)) {
    markPermissionAsked(userId);
    try {
      await Notification.requestPermission();
    } catch (error) {
      return { status: 'permission_request_failed' };
    }
  }

  if (Notification.permission !== 'granted') {
    return { status: 'permission_not_granted' };
  }

  const registration = await navigator.serviceWorker.register('/push-sw.js');
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
  }

  await axiosInstance.post('notifications/push/subscribe', {
    subscription: subscription.toJSON()
  });

  return { status: 'subscribed' };
};

export const detachPushSubscription = async () => {
  if (!isPushSupported()) {
    return { status: 'unsupported' };
  }

  const registration = await navigator.serviceWorker.getRegistration('/push-sw.js');
  if (!registration) {
    return { status: 'no_registration' };
  }

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    return { status: 'no_subscription' };
  }

  const endpoint = subscription.endpoint;

  try {
    await axiosInstance.post('notifications/push/unsubscribe', { endpoint });
  } catch (error) {
    // Continue with browser unsubscribe even if backend call fails.
  }

  try {
    await subscription.unsubscribe();
  } catch (error) {
    return { status: 'unsubscribe_failed' };
  }

  return { status: 'unsubscribed' };
};
